import { cloneElement, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';

// Dymek z treścią interaktywną — inaczej niż Tooltip.
//
// Tooltip ma `pointer-events-none` i znika, gdy tylko kursor opuści element
// wyzwalający. To dobre dla krótkiego opisu, ale uniemożliwia kliknięcie
// czegokolwiek w środku: przesuwając mysz w stronę linku, użytkownik zamyka
// dymek, zanim do niego dotrze.
//
// Tutaj kursor może przejechać z elementu wyzwalającego na kartę — zamknięcie
// jest opóźnione o krótką chwilę i anulowane, gdy mysz wejdzie na kartę.

const CLOSE_DELAY_MS = 180;
const CARD_WIDTH = 288; // odpowiada klasie w-72
const VIEWPORT_MARGIN = 12;
// Zapas potrzebny, żeby zdecydować, czy karta zmieści się nad elementem.
const ESTIMATED_HEIGHT = 220;

// Po dotknięciu ekranu przeglądarka wysyła jeszcze zgodnościowe zdarzenia myszy
// (mouseover). Przez ten czas ignorujemy je, żeby nie otwierały karty tuż po tym,
// jak dotknięcie ją zamknęło.
const TOUCH_GUARD_MS = 600;

export function HoverCard({ children, content, className }) {
  const [placement, setPlacement] = useState(null);
  const triggerRef = useRef(null);
  const cardRef = useRef(null);
  const closeTimer = useRef(null);
  const lastTouchAt = useRef(0);
  // Stan otwarcia trzymany również w ref: handlery zdarzeń natywnych czytają go
  // synchronicznie, bez czekania na ponowny render.
  const isOpenRef = useRef(false);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const close = useCallback(() => {
    cancelClose();
    isOpenRef.current = false;
    setPlacement(null);
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      isOpenRef.current = false;
      setPlacement(null);
    }, CLOSE_DELAY_MS);
  }, [cancelClose]);

  const open = useCallback(() => {
    cancelClose();
    isOpenRef.current = true;

    const element = triggerRef.current;
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const above = rect.top > ESTIMATED_HEIGHT;

    // Wyśrodkowanie na elemencie, ale przycięte do widocznego obszaru —
    // stopka logowania stoi przy krawędzi ekranu i karta bez tego
    // wychodziłaby poza okno.
    const half = CARD_WIDTH / 2;
    const left = Math.min(
      Math.max(rect.left + rect.width / 2, half + VIEWPORT_MARGIN),
      window.innerWidth - half - VIEWPORT_MARGIN
    );

    setPlacement({
      left,
      top: above ? rect.top - 8 : rect.bottom + 8,
      above,
    });
  }, [cancelClose]);

  // Sprzątanie licznika przy odmontowaniu — inaczej setState poleciałby
  // do komponentu, którego już nie ma.
  useEffect(() => cancelClose, [cancelClose]);

  useEffect(() => {
    if (!placement) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') close();
    };

    // Dotknięcie lub kliknięcie poza kartą zamyka ją — na ekranie dotykowym
    // to jedyny sposób, bo nie ma zdarzenia opuszczenia elementu myszą.
    const onPointerDownOutside = (event) => {
      if (triggerRef.current?.contains(event.target)) return;
      if (cardRef.current?.contains(event.target)) return;
      close();
    };

    window.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDownOutside);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDownOutside);
    };
  }, [placement, close]);

  const isTouchRecent = () => Date.now() - lastTouchAt.current < TOUCH_GUARD_MS;

  const trigger = cloneElement(children, {
    ref: triggerRef,
    // Mysz obsługiwana wyłącznie najechaniem. Świadomie nie ma tu onClick:
    // przy prawdziwym kliknięciu przeglądarka wysyła najpierw mouseover, który
    // kartę otwiera, a dopiero potem click — przełącznik na kliknięciu
    // zamykałby więc to, co przed chwilą otworzyło najechanie.
    onMouseEnter: () => {
      if (!isTouchRecent()) open();
    },
    onMouseLeave: () => {
      if (!isTouchRecent()) scheduleClose();
    },
    onFocus: open,
    // Dotyk: pointerdown wyprzedza zgodnościowe zdarzenia myszy, więc to on
    // decyduje o przełączeniu, a znacznik czasu wycisza to, co przyjdzie po nim.
    onPointerDown: (event) => {
      if (event.pointerType !== 'touch') return;
      lastTouchAt.current = Date.now();
      if (isOpenRef.current) close();
      else open();
    },
    'aria-expanded': Boolean(placement),
    'aria-haspopup': 'dialog',
  });

  return (
    <>
      {trigger}
      {placement &&
        createPortal(
          // Dwa elementy zamiast jednego, i to jest konieczne: zewnętrzny
          // ustawia pozycję przez `transform`, wewnętrzny odtwarza animację
          // wejścia, która również animuje `transform`. Na jednym elemencie
          // animacja nadpisywała pozycjonowanie i karta na czas jej trwania
          // lądowała w złym miejscu — nachodząc na element wyzwalający.
          <div
            ref={cardRef}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            onFocusCapture={cancelClose}
            style={{
              position: 'fixed',
              left: placement.left,
              top: placement.top,
              transform: placement.above ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
              width: CARD_WIDTH,
            }}
            className="z-[100]"
          >
            <div
              role="dialog"
              className={cn(
                'animate-scale-in rounded-lg border border-line bg-surface p-3 shadow-pop',
                className
              )}
            >
              {content}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

export default HoverCard;
